import { useEffect, useState } from "react";
import TrustBadges from "../components/TrustBadges";

function ProfilePage({
  auth,
  error,
  isLoading,
  isSaving,
  notice,
  onSave,
  onUploadAvatar,
  profile,
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [form, setForm] = useState(profileForm(profile, auth));

  useEffect(() => {
    if (!isEditing) {
      setForm(profileForm(profile, auth));
    }
  }, [auth, isEditing, profile]);

  async function submitProfile(event) {
    event.preventDefault();
    const saved = await onSave({
      display_name: form.display_name.trim(),
      full_name: optionalValue(form.full_name),
      phone_number: optionalValue(form.phone_number),
      location: optionalValue(form.location),
    });
    if (saved) {
      setIsEditing(false);
    }
  }
  const displayName = profile?.display_name || auth?.user?.display_name || "Profil";
  const strength = profileStrength(profile, auth);

  return (
    <section className="main-panel">
      <div className="profile-hero">
        {profile?.avatar_url ? <img className="profile-avatar profile-avatar-image" src={profile.avatar_url} alt={`${displayName} profilbillede`} /> : <div className="profile-avatar">{initials(displayName)}</div>}
        <div>
          <span className="mini-label">{displayName}</span>
          <h1>{displayName}</h1>
          <p>{profileSummary(profile, auth)}</p>
          <TrustBadges
            completedSalesCount={profile?.completed_sales_count || 0}
            ratingAverage={profile?.rating_average || 0}
            ratingCount={profile?.rating_count || 0}
            verified={Boolean(profile?.email_verified || profile?.phone_verified || auth?.user?.email_verified)}
          />
        </div>
        <button className="secondary-action" onClick={() => setIsEditing((value) => !value)} type="button">
          {isEditing ? "Luk redigering" : "Rediger profil"}
        </button>
      </div>

      {error && <p className="status-line error">{error}</p>}
      {notice && <p className="status-line notice">{notice}</p>}

      {isEditing && (
        <form className="profile-edit-form" onSubmit={submitProfile}>
          <div className="field-row">
            <label>Visningsnavn<input maxLength="120" onChange={(event) => setForm({ ...form, display_name: event.target.value })} required value={form.display_name} /></label>
            <label>Fulde navn<input maxLength="160" onChange={(event) => setForm({ ...form, full_name: event.target.value })} value={form.full_name} /></label>
          </div>
          <div className="field-row">
            <label>Telefon<input maxLength="40" onChange={(event) => setForm({ ...form, phone_number: event.target.value })} type="tel" value={form.phone_number} /></label>
            <label>By<input maxLength="120" onChange={(event) => setForm({ ...form, location: event.target.value })} value={form.location} /></label>
          </div>
          <label className="profile-avatar-upload">Profilbillede<input accept="image/jpeg,image/png,image/webp" disabled={isSaving} onChange={(event) => {
            const avatar = event.target.files?.[0];
            if (avatar) onUploadAvatar(avatar);
          }} type="file" /></label>
          <div className="profile-edit-actions">
            <button className="secondary-action" disabled={isSaving} onClick={() => setIsEditing(false)} type="button">Annuller</button>
            <button className="primary-action" disabled={isSaving} type="submit">{isSaving ? "Gemmer..." : "Gem profil"}</button>
          </div>
        </form>
      )}

      <div className="profile-grid">
        <article className="profile-card profile-strength-card">
          <span className="mini-label">Profilstyrke</span>
          <div className="profile-strength-score">{isLoading ? "..." : `${strength.score}%`}</div>
          <div className="profile-strength-bar" aria-label="Profilstyrke">
            <span style={{ width: `${strength.score}%` }} />
          </div>
          <p>{strength.label}</p>
          {strength.missing.length > 0 && (
            <ul className="profile-checklist">
              {strength.missing.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          )}
        </article>

        <article className="profile-card">
          <span className="mini-label">Rating</span>
          <div className="profile-rating">
            {profile?.rating_count ? profile.rating_average.toFixed(1) : "-"}
          </div>
          <p>{ratingText(profile)}</p>
        </article>

        <article className="profile-card">
          <span className="mini-label">Mine tal</span>
          <div className="profile-stats">
            <span>Solgt</span><strong>{profile?.completed_sales_count || 0}</strong>
            <span>Købt</span><strong>{profile?.completed_purchases_count || 0}</strong>
            <span>Email</span><strong>{profile?.email_verified || auth?.user?.email_verified ? "Verificeret" : "Mangler"}</strong>
            <span>Telefon</span><strong>{profile?.phone_verified ? "Verificeret" : "Valgfri"}</strong>
          </div>
        </article>

      </div>
    </section>
  );
}

function profileForm(profile, auth) {
  return {
    display_name: profile?.display_name || auth?.user?.display_name || "",
    full_name: profile?.full_name || "",
    phone_number: profile?.phone_number || "",
    location: profile?.location || "",
  };
}

function optionalValue(value) {
  return value.trim() || null;
}

function profileStrength(profile, auth) {
  const checks = [
    {
      done: Boolean(profile?.display_name || auth?.user?.display_name),
      missing: "Tilføj visningsnavn",
      weight: 15,
    },
    {
      done: Boolean(profile?.email_verified || auth?.user?.email_verified),
      missing: "Verificer email",
      weight: 20,
    },
    {
      done: Boolean(profile?.location),
      missing: "Tilføj by",
      weight: 20,
    },
    {
      done: Boolean(profile?.avatar_url),
      missing: "Tilføj profilbillede",
      weight: 15,
    },
    {
      done: Boolean(profile?.phone_verified),
      missing: "Verificer telefon, hvis du vil styrke tilliden",
      weight: 10,
    },
    {
      done: (profile?.completed_sales_count || 0) > 0 || (profile?.rating_count || 0) > 0,
      missing: "Gennemfor din forste handel",
      weight: 20,
    },
  ];
  const score = checks.reduce((total, check) => total + (check.done ? check.weight : 0), 0);
  const missing = checks.filter((check) => !check.done).map((check) => check.missing).slice(0, 3);

  return {
    label: score >= 80 ? "Stark profil" : score >= 50 ? "God start" : "Kan styrkes",
    missing,
    score,
  };
}

function profileSummary(profile, auth) {
  const verified = profile?.email_verified || profile?.phone_verified || auth?.user?.email_verified;
  const location = profile?.location || "By ikke angivet";
  return `${verified ? "Verificeret konto" : "Konto ikke verificeret"} | ${location}`;
}

function ratingText(profile) {
  if (!profile?.rating_count) {
    return "Ingen rating endnu.";
  }

  return `Baseret paa ${profile.rating_count} gennemforte handler.`;
}

function initials(name) {
  return (name || "P")
    .split(" ")
    .map((part) => part.trim()[0])
    .filter(Boolean)
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

export default ProfilePage;
